/**
 * Espelha no localStorage o "histórico" da jornada de compra do cliente (pedido em andamento, carrinho,
 * filtros da loja e preenchimentos) que o app guarda no sessionStorage.
 *
 * Por quê: o sessionStorage é perdido quando o cliente volta pelo botão do aparelho/do app, quando o
 * navegador do celular descarta a aba, ou quando cada link abre numa página nova (navegador do Instagram).
 * Sem ele o carrinho "sumia" e cada página criava um pedido novo. O localStorage sobrevive a tudo isso.
 *
 * Regras de segurança do espelho:
 *  - validade curta (pedido em andamento: 3 h; demais: 12 h);
 *  - só restaura o carrinho/filtros se a loja da URL for a mesma da gravação (quando a URL identifica a loja);
 *  - não restaura nada de pedido já pago/cancelado (verificado em segundo plano);
 *  - remover a chave da sessão (limpar carrinho, novo pedido, compra concluída) remove também o espelho;
 *  - nunca em modo proposta do fornecedor (?mode=orcamento).
 */

const ID_KEY = 'ameup_parent_event_id';
const CODE_KEY = 'ameup_event_code';
const CART_KEY = 'supplier_cart_items';
const MIRROR_PREFIX = 'ameup_mirror_v2:';
const LEGACY_KEY = 'ameup_parent_session_v1';

const HOUR = 60 * 60 * 1000;
const TTL_PARENT = 3 * HOUR;
const TTL_DEFAULT = 12 * HOUR;

/** Chaves da jornada do CLIENTE que devem sobreviver à perda da sessão. */
const EXACT_KEYS = new Set([
  ID_KEY,
  CODE_KEY,
  CART_KEY,
  'ameup_special_guests',
  'ameup_event_type',
  'ameup_deferred_pix_days',
  'ameup_simulation_id',
  'ameup_referred_by',
  'ameup_referral_discount',
  'ameup_referral_code_id',
  'ameup_last_supplier_slug',
  'ameup_last_product_slug',
  'ameup_checkout_tax',
]);
const PREFIX_KEYS = ['store_filters_', 'ameup_infos_draft:store:'];

/**
 * NUNCA espelhar a identidade da PROPOSTA compartilhada pelo fornecedor (nome do cliente/desconto):
 * ela vale só para a sessão aberta pelo link da proposta. Espelhada, aparecia depois na raiz da loja
 * ("Olá, <cliente>! Confira a proposta que fizemos para você") para qualquer visita nas horas seguintes.
 */
const NEVER_MIRROR_KEYS = ['shared_cart_client_name', 'shared_cart_discount'];

const isMirrored = (key: string) => EXACT_KEYS.has(key) || PREFIX_KEYS.some((p) => key.startsWith(p));
const ttlFor = (key: string) => (key === ID_KEY || key === CODE_KEY ? TTL_PARENT : TTL_DEFAULT);

interface MirrorRecord {
  v: string;
  t: number;
  slug?: string | null;
}

const currentSlug = (): string | null => {
  const m = /^\/(?:loja|cardapio)\/([^/]+)/.exec(window.location.pathname);
  return m ? decodeURIComponent(m[1]) : null;
};

const isSupplierQuoteMode = () => {
  try {
    return new URLSearchParams(window.location.search).get('mode') === 'orcamento';
  } catch {
    return false;
  }
};

const mirrorWrite = (key: string, value: string) => {
  try {
    const rec: MirrorRecord = { v: value, t: Date.now(), slug: currentSlug() };
    localStorage.setItem(MIRROR_PREFIX + key, JSON.stringify(rec));
  } catch { /* localStorage indisponível */ }
};

const mirrorRemove = (key: string) => {
  try { localStorage.removeItem(MIRROR_PREFIX + key); } catch { /* sem localStorage */ }
};

const mirrorClearAll = () => {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(MIRROR_PREFIX))
      .forEach((k) => localStorage.removeItem(k));
  } catch { /* sem localStorage */ }
};

/** O carrinho espelhado só vale para a loja em que foi gravado. */
const cartMatchesSlug = (value: string, slug: string | null): boolean => {
  if (!slug) return true;
  try {
    const items = JSON.parse(value);
    if (!Array.isArray(items) || items.length === 0) return true;
    const known = items.map((i: any) => i?.supplierSlug).filter(Boolean);
    return known.length === 0 || known.includes(slug);
  } catch {
    return true;
  }
};

let installed = false;

/** Instala o espelho e restaura a jornada, se a sessão estiver vazia. Chamar uma vez no boot, antes das telas. */
export function installParentSessionMirror(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  try { localStorage.removeItem(LEGACY_KEY); } catch { /* ok */ }
  // Limpa espelhos antigos da proposta compartilhada (gravados por versões anteriores).
  try { NEVER_MIRROR_KEYS.forEach((k) => localStorage.removeItem(MIRROR_PREFIX + k)); } catch { /* ok */ }

  let originalSet: (key: string, value: string) => void;
  try {
    const proto = Storage.prototype;
    originalSet = proto.setItem;
    const originalRemove = proto.removeItem;

    proto.setItem = function (key: string, value: string) {
      originalSet.call(this, key, value);
      try {
        if (this === window.sessionStorage && isMirrored(key) && !isSupplierQuoteMode()) mirrorWrite(key, String(value));
      } catch { /* o espelho nunca pode quebrar o app */ }
    };

    proto.removeItem = function (key: string) {
      originalRemove.call(this, key);
      try {
        if (this === window.sessionStorage && isMirrored(key)) mirrorRemove(key);
      } catch { /* idem */ }
    };
  } catch {
    return; // ambiente sem Storage.prototype gravável
  }

  // Restauração: só chaves AUSENTES na sessão, recentes e da mesma loja.
  try {
    if (isSupplierQuoteMode()) return;
    const slug = currentSlug();
    let restoredParentId: string | null = null;

    Object.keys(localStorage)
      .filter((k) => k.startsWith(MIRROR_PREFIX))
      .forEach((storageKey) => {
        const key = storageKey.slice(MIRROR_PREFIX.length);
        if (!isMirrored(key)) return;
        let rec: MirrorRecord | null = null;
        try { rec = JSON.parse(localStorage.getItem(storageKey) || 'null'); } catch { rec = null; }
        if (!rec || typeof rec.v !== 'string') { localStorage.removeItem(storageKey); return; }
        if (Date.now() - rec.t > ttlFor(key)) { localStorage.removeItem(storageKey); return; }
        if (sessionStorage.getItem(key) !== null) return;
        if (slug && rec.slug && slug !== rec.slug) return;
        if (key === CART_KEY && !cartMatchesSlug(rec.v, slug)) return;

        originalSet.call(sessionStorage, key, rec.v);
        if (key === ID_KEY) restoredParentId = rec.v;
      });

    // Em segundo plano: descarta a jornada restaurada se o pedido já foi pago/cancelado.
    if (restoredParentId) {
      const id = restoredParentId;
      void (async () => {
        try {
          const { supabase } = await import('@/integrations/supabase/client');
          const { data } = await (supabase as any)
            .from('events')
            .select('status, status_payments, is_active')
            .eq('id', id)
            .maybeSingle();
          if (!data) return; // ilegível agora (RLS/JWT): mantém
          const paid = ['paid', 'pago', 'approved', 'partially_paid'].includes(String(data.status_payments || '').toLowerCase());
          const closed = data.is_active === false || /cancel/i.test(String(data.status || ''));
          if (paid || closed) {
            [ID_KEY, CODE_KEY, CART_KEY].forEach((k) => sessionStorage.removeItem(k));
            mirrorClearAll();
          }
        } catch { /* sem validação: mantém */ }
      })();
    }
  } catch { /* sem storage */ }
}
